import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { apiRequest } from '~/lib/api/client';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.task-automation.module.css';

/**
 * `/admin/task-automation` — событийные авто-правила + шаблоны полей
 * (FE-S15-6/7/8/9 compact: список + создание + переключение/удаление).
 */

const AutoRule = z
  .object({
    id: z.string(),
    name: z.string(),
    event_kind: z.string(),
    is_active: z.boolean(),
  })
  .passthrough();
const AutoRulesResponse = z
  .object({ auto_rules: z.array(AutoRule) })
  .transform((d) => d.auto_rules);

const FieldTemplate = z
  .object({
    id: z.string(),
    key: z.string(),
    label: z.string(),
    type: z.string(),
  })
  .passthrough();
const TemplatesResponse = z
  .object({ field_templates: z.array(FieldTemplate) })
  .transform((d) => d.field_templates);

const EmailPreview = z.object({ trigger: z.string(), subject: z.string(), html: z.string() });
const EmailPreviewsResponse = z
  .object({ previews: z.array(EmailPreview) })
  .transform((d) => d.previews);

const EVENT_KINDS = [
  { value: 'anomaly.created', label: m.admin_automation_event_anomaly() },
  { value: 'source_conflict.created', label: m.admin_automation_event_conflict() },
  { value: 'approval.requested', label: m.admin_automation_event_approval() },
];

const FIELD_TYPES = ['text', 'number', 'decimal', 'date', 'checkbox', 'url', 'single_select'];

const RULES_KEY = ['task-module', 'admin', 'auto-rules'];
const TPL_KEY = ['task-module', 'admin', 'field-templates'];

export const Route = createFileRoute('/_auth/admin/task-automation')({
  component: TaskAutomationAdmin,
});

function TaskAutomationAdmin(): JSX.Element {
  const qc = useQueryClient();

  // ─── Auto-rules ───
  const rulesQuery = useQuery({
    queryKey: RULES_KEY,
    queryFn: () => apiRequest('/task-module/auto-rules', AutoRulesResponse),
  });
  const [ruleName, setRuleName] = useState('');
  const [eventKind, setEventKind] = useState(EVENT_KINDS[0]?.value ?? '');
  const [ruleProjectId, setRuleProjectId] = useState('');
  const [titleTemplate, setTitleTemplate] = useState(
    m.admin_automation_task_title_default({
      subject_type: '{subject_type}',
      subject_id: '{subject_id}',
    }),
  );

  const createRule = useMutation({
    mutationFn: () =>
      apiRequest('/task-module/auto-rules', z.unknown(), {
        method: 'POST',
        body: {
          name: ruleName.trim(),
          event_kind: eventKind,
          conditions: [],
          task_template: {
            title_template: titleTemplate,
            priority: 'high',
            project_id: ruleProjectId,
            assignee_role: null,
            due_offset_days: 3,
          },
        },
      }),
    onSuccess: () => {
      setRuleName('');
      qc.invalidateQueries({ queryKey: RULES_KEY });
    },
  });

  async function toggleRule(id: string, isActive: boolean) {
    await apiRequest(`/task-module/auto-rules/${id}`, z.unknown(), {
      method: 'PATCH',
      body: { is_active: !isActive },
    });
    qc.invalidateQueries({ queryKey: RULES_KEY });
  }
  async function removeRule(id: string) {
    await apiRequest(`/task-module/auto-rules/${id}`, z.unknown(), { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: RULES_KEY });
  }

  // ─── Field templates ───
  const tplQuery = useQuery({
    queryKey: TPL_KEY,
    queryFn: () => apiRequest('/task-module/field-templates', TemplatesResponse),
  });
  const [tplKey, setTplKey] = useState('');
  const [tplLabel, setTplLabel] = useState('');
  const [tplType, setTplType] = useState('text');

  const createTpl = useMutation({
    mutationFn: () =>
      apiRequest('/task-module/field-templates', z.unknown(), {
        method: 'POST',
        body: { key: tplKey.trim(), label: tplLabel.trim(), type: tplType },
      }),
    onSuccess: () => {
      setTplKey('');
      setTplLabel('');
      qc.invalidateQueries({ queryKey: TPL_KEY });
    },
  });

  async function removeTpl(id: string) {
    await apiRequest(`/task-module/field-templates/${id}`, z.unknown(), { method: 'DELETE' });
    qc.invalidateQueries({ queryKey: TPL_KEY });
  }

  return (
    <div className={shared.page}>
      <h1 className={styles.title}>
        <Zap size={20} />
        {m.ui_avtomatizaciya_zadach()}
      </h1>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{m.ui_sobytiynye_pravila()}</h2>
        <div className={styles.form}>
          <input
            className={styles.input}
            placeholder={m.admin_automation_rule_name()}
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
          />
          <div className={styles.chipRow} role="radiogroup" aria-label={m.admin_automation_event()}>
            {EVENT_KINDS.map((ek) => (
              <button
                key={ek.value}
                type="button"
                role="radio"
                aria-checked={eventKind === ek.value}
                className={[styles.chip, eventKind === ek.value ? styles.chipActive : ''].join(' ')}
                onClick={() => setEventKind(ek.value)}
              >
                {ek.label}
              </button>
            ))}
          </div>
          <input
            className={styles.input}
            placeholder={m.admin_automation_project_uuid()}
            value={ruleProjectId}
            onChange={(e) => setRuleProjectId(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder={m.admin_automation_task_title_placeholder()}
            value={titleTemplate}
            onChange={(e) => setTitleTemplate(e.target.value)}
          />
          <button
            type="button"
            className={styles.create}
            disabled={!ruleName.trim() || !ruleProjectId.trim() || createRule.isPending}
            onClick={() => createRule.mutate()}
          >
            {m.ui_sozdat_pravilo()}
          </button>
        </div>
        <ul className={styles.list}>
          {(rulesQuery.data ?? []).map((r) => (
            <li key={r.id} className={styles.item}>
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={r.is_active}
                  onChange={() => void toggleRule(r.id, r.is_active)}
                />
                <span className={styles.itemName}>{r.name}</span>
              </label>
              <code className={styles.itemMeta}>{r.event_kind}</code>
              <button
                type="button"
                className={styles.remove}
                aria-label={m.admin_automation_delete_rule()}
                onClick={() => void removeRule(r.id)}
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{m.ui_shablony_poley_workspace()}</h2>
        <div className={styles.formRow}>
          <input
            className={styles.input}
            placeholder="key (snake_case)…"
            value={tplKey}
            onChange={(e) => setTplKey(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder={m.admin_automation_signature()}
            value={tplLabel}
            onChange={(e) => setTplLabel(e.target.value)}
          />
          <div className={styles.chipRow} role="radiogroup" aria-label={m.admin_automation_type()}>
            {FIELD_TYPES.map((ft) => (
              <button
                key={ft}
                type="button"
                role="radio"
                aria-checked={tplType === ft}
                className={[styles.chip, tplType === ft ? styles.chipActive : ''].join(' ')}
                onClick={() => setTplType(ft)}
              >
                {ft}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={styles.create}
            disabled={!tplKey.trim() || !tplLabel.trim() || createTpl.isPending}
            onClick={() => createTpl.mutate()}
          >
            {m.ui_sozdat()}
          </button>
        </div>
        <ul className={styles.list}>
          {(tplQuery.data ?? []).map((t) => (
            <li key={t.id} className={styles.item}>
              <code className={styles.itemMeta}>{t.key}</code>
              <span className={styles.itemName}>{t.label}</span>
              <span className={styles.itemMeta}>{t.type}</span>
              <button
                type="button"
                className={styles.remove}
                aria-label={m.admin_automation_delete_template()}
                onClick={() => void removeTpl(t.id)}
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* FE-S14-10 — предпросмотр email-шаблонов уведомлений */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{m.ui_shablony_pisem_predprosmotr()}</h2>
        <EmailPreviewsSection />
      </section>
    </div>
  );
}

// Карта «ключ → подпись» живёт функцией, а не константой-словарём:
// локальный `Record<string, string>` — та же захардкоженная строка,
// только спрятанная в объект, и он же мешает подмене при смене локали.
function triggerLabel(key: string): string {
  switch (key) {
    case 'assigned_to_me':
      return m.ui_naznachenie();
    case 'mention':
      return m.ui_upominanie();
    case 'due_soon':
      return m.ui_priblizhaetsya_srok();
    case 'overdue':
      return m.ui_prosrocheno();
    default:
      return key;
  }
}

function EmailPreviewsSection(): JSX.Element {
  const [selected, setSelected] = useState<string>('assigned_to_me');
  const previewsQuery = useQuery({
    queryKey: ['task-module', 'email-previews'],
    queryFn: () => apiRequest('/task-module/email-previews', EmailPreviewsResponse),
  });
  const previews = previewsQuery.data ?? [];
  const current = previews.find((pv) => pv.trigger === selected);

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={m.admin_automation_email_template()}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}
      >
        {previews.map((pv) => (
          <button
            key={pv.trigger}
            type="button"
            role="radio"
            aria-checked={selected === pv.trigger}
            className={styles.previewTab}
            data-active={selected === pv.trigger}
            onClick={() => setSelected(pv.trigger)}
          >
            {triggerLabel(pv.trigger) ?? pv.trigger}
          </button>
        ))}
      </div>
      {current ? (
        <div className={styles.previewBox}>
          <div className={styles.previewSubject}>{current.subject}</div>
          <iframe
            title={m.admin_automation_email_preview()}
            srcDoc={current.html}
            className={styles.previewFrame}
            sandbox=""
          />
        </div>
      ) : (
        <span className={styles.previewEmpty}>{m.ui_net_dannyh_predprosmotra()}</span>
      )}
    </div>
  );
}
